/**
 * Tenant Config — Initializes runtime URL overrides from Electron tenant IPC.
 *
 * When running in Electron with a tenant selected, this loads the tenant's URLs
 * into window.__CW_TENANT_URLS__ so that all process.env.REACT_APP_* references
 * (rewritten by Vite's define plugin) resolve to tenant-specific values.
 */

import { trace } from "../utils/debugTrace";

const tenantConfig = {
  /**
   * Load active tenant URLs into window.__CW_TENANT_URLS__.
   * Returns the active tenant object or null.
   */
  async initialize() {
    if (!window.electronAPI?.tenant) {
      trace("BootDiag", "tenantConfig: no electronAPI.tenant", { hasElectronAPI: !!window.electronAPI });
      return null;
    }

    try {
      const active = await window.electronAPI.tenant.getActive();
      trace("BootDiag", "tenantConfig: getActive()", { code: active?.code || null });
      if (!active) return null;

      // Cache the resolved code (unprefixed — "cw-active-tenant" is a
      // GLOBAL_KEY in tenantStorage.js) so the next boot can apply the
      // tenant-prefixed localStorage namespace synchronously, without
      // waiting on this same IPC round-trip to resolve first. See App.js.
      try {
        localStorage.setItem("cw-active-tenant", active.code);
      } catch {
        // Storage unavailable — next boot falls back to the IPC call only, same as before.
      }

      const urls = await window.electronAPI.tenant.getUrls(active.code);
      if (urls && Object.keys(urls).length > 0) {
        window.__CW_TENANT_URLS__ = urls;
      } else {
        console.warn(`[TenantConfig] No URLs found for tenant '${active.code}'`);
      }
      trace("BootDiag", "tenantConfig: initialize done", { code: active.code, hasUrls: !!(urls && Object.keys(urls).length > 0) });

      return active;
    } catch (err) {
      console.error("[TenantConfig] Failed to initialize:", err);
      trace("BootDiag", "tenantConfig: initialize threw", { error: err?.message });
      return null;
    }
  },

  /**
   * Clear tenant URL overrides (used when switching tenants before reload).
   */
  clear() {
    delete window.__CW_TENANT_URLS__;
  },
};

export default tenantConfig;
